import React from "react";
import { ShieldCheck, Clock, CheckCircle, Lock, Sparkles } from "lucide-react";

/**
 * Accurate, non-misleading provider trust badges.
 *
 * Trust messaging policy (Feb 2026 — locked):
 *   - Verified Provider      (green)   → provider.is_id_verified === true
 *   - Verification Pending   (amber)   → provider.is_id_verified === false (and status='approved')
 *   - Carefully Reviewed     (blue)    → public, approved providers (when `showCarefullyReviewed`)
 *   - New Provider           (neutral) → provider.review_count === 0 (and status='approved')
 *
 * REMOVED (Feb 2026): "Business Registered" and "Insured" badges. HandyHub
 * no longer collects business registration or insurance documents. The
 * ID Verified badge is the single trust signal, complemented by "Carefully
 * Reviewed" for approved providers. Customers should independently assess
 * providers before engaging services (see footer disclaimer).
 *
 * `size` controls icon size only ("sm" | "md").
 */
export const ProviderBadges = ({
  provider,
  size = "sm",
  showCarefullyReviewed = false,
  showNew = true,
  className = "",
}) => {
  if (!provider) return null;
  const iconSize = size === "md" ? 14 : 12;
  const isApproved = !provider.status || provider.status === "approved";

  return (
    <div className={`provider-trust-badges ${className}`} data-testid="provider-trust-badges">
      {provider.is_id_verified ? (
        <span className="trust-badge trust-badge-green" data-testid="badge-verified">
          <ShieldCheck size={iconSize} strokeWidth={2.5} />
          ID Verified
        </span>
      ) : isApproved ? (
        <span className="trust-badge trust-badge-amber" data-testid="badge-verification-pending">
          <Clock size={iconSize} strokeWidth={2.5} />
          Verification Pending
        </span>
      ) : null}

      {showCarefullyReviewed && isApproved && (
        <span className="trust-badge trust-badge-blue" data-testid="badge-carefully-reviewed">
          <CheckCircle size={iconSize} strokeWidth={2.5} />
          Carefully Reviewed
        </span>
      )}

      {showNew && isApproved && (provider.review_count ?? 0) === 0 && (
        <span className="trust-badge trust-badge-neutral" data-testid="badge-new">
          <Sparkles size={iconSize} strokeWidth={2.5} />
          New Provider
        </span>
      )}
    </div>
  );
};

/** Standalone "Payments secured by Stripe" trust line. */
export const StripeSecuredBadge = ({ className = "" }) => (
  <span className={`trust-badge trust-badge-locked ${className}`} data-testid="badge-stripe-secured">
    <Lock size={12} strokeWidth={2.5} />
    Payments Secured by Stripe
  </span>
);

export default ProviderBadges;
